import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBasket, Trophy } from 'lucide-react';
import { useSelector } from 'react-redux';
import { formatDistanceToNow } from 'date-fns';

const escapeRegExp = (string) => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

const ItemCard = ({ item, onDelete, searchTerm, onClick, showTooltipInStudyMode }) => {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [basketName, setBasketName] = useState('Loading...');
  const titleRef = useRef(null);

  const baskets = useSelector(state => state.workspaces.currentWorkspace.baskets);

  useEffect(() => {
    if (item.basket_id && baskets.length > 0) {
      const basket = baskets.find(b => b.id === item.basket_id);
      setBasketName(basket ? basket.name : 'Unknown Basket');
    } else if (!item.basket_id) {
      setBasketName('No Basket');
    }
  }, [item.basket_id, baskets]);

  useEffect(() => {
    const element = titleRef.current;
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [item.name]);

  const handleClick = () => {
    navigate(`/topic-detail/${item.id}`, {
      state: { 
        topic: item,
        basket: {
          id: item.basket_id,
          name: basketName
        },
        searchTerm: searchTerm
      } 
    });
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete(item.id);
  };

  const highlightedConcept = item.concepts && item.concepts.find(c => c.is_highlighted);
  const tag = item.tag || (item.tags && item.tags.length > 0 ? item.tags[0] : null);

  const highlightText = (text, term) => {
    if (!term || !text) return text;
    try {
      const escapedTerm = escapeRegExp(term);
      const regex = new RegExp(`(${escapedTerm})`, 'gi');
      const parts = text.split(regex);
      
      return parts.map((part, index) => {
        if (part.toLowerCase() === term.toLowerCase()) {
          return <mark key={index} className="bg-yellow-200">{part}</mark>;
        }
        return part;
      });
    } catch (error) {
      console.error('Error highlighting text:', error);
      return text;
    }
  };

  const matchingConcept = searchTerm && item.concepts && item.concepts.find(c => 
    c.text.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    return formatDistanceToNow(new Date(dateString), { addSuffix: true });
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-sm p-3 mb-3 transition-all duration-200 
                 cursor-pointer border border-neutral-900 hover:shadow-lg relative"
      onClick={handleClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowTooltip(false);
      }}
      style={{ zIndex: showTooltip ? 20 : 10 }}
    >
      {isHovered && onDelete && (
        <button
          className="absolute top-1 right-1 text-gray-500 hover:text-red-500 transition-colors duration-200"
          onClick={handleDelete}
        >
          <Trash2 size={16} />
        </button>
      )}
      <div className="flex flex-col h-full min-h-[100px]">
        <div className="flex-1">
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0 relative">
              <div className="flex items-center gap-1">
                <h3 
                  ref={titleRef}
                  className="font-semibold text-sm text-gray-800 truncate"
                  onMouseEnter={() => isTruncated && setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  {highlightText(item.name, searchTerm)}
                </h3>
                {item.is_master && (
                  <Trophy size={14} className="text-yellow-500 flex-shrink-0" />
                )}
              </div>
              {showTooltip && isTruncated && (
                <div className={`absolute bg-gray-900 text-white text-xs rounded py-1 px-2 -top-8 left-0 whitespace-nowrap shadow-lg ${showTooltipInStudyMode ? 'z-[150]' : 'z-[100]'}`}>
                  {item.name}
                </div>
              )}
            </div>
            {tag && (
              <div className="flex-shrink-0">
                <span 
                  className="inline-block text-xs px-2 py-0.5 rounded-full max-w-[120px] truncate"
                  style={{
                    backgroundColor: tag.color || '#e5e5e5',
                    color: tag.color ? `${getContrastColor(tag.color)}CC` : '#000000CC',
                    fontWeight: 'normal'
                  }}
                >
                  {tag.name}
                </span>
              </div>
            )}
          </div>
          {(highlightedConcept || matchingConcept) && (
            <p className="text-xs text-gray-600 italic line-clamp-2 mt-2 break-words">
              "{highlightText((matchingConcept || highlightedConcept).text, searchTerm)}"
            </p>
          )}
        </div>
        <div className="flex items-end justify-between mt-auto pt-2">
          <div className="flex-shrink-0">
            {item.basket_id && (
              <div className="flex items-center space-x-1 text-blue-600">
                <ShoppingBasket size={12} />
                <span className="text-xs italic truncate max-w-[150px]">{basketName}</span>
              </div>
            )}
          </div>
          <div className="text-[11px] text-gray-400">
            {item.updated_at && item.updated_at !== item.created_at
              ? formatDate(item.updated_at)
              : formatDate(item.created_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Keep the existing getContrastColor function
const getContrastColor = (hexColor) => {
  // Remove the hash if it's there
  hexColor = hexColor.replace('#', '');

  // Convert to RGB
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black for light colors, white for dark colors
  return luminance > 0.5 ? '#000000' : '#ffffff';
};

export default ItemCard;
